// src/components/GarmentMesh.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF, useTexture } from "@react-three/drei";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { cacheRestPose, restoreRestPose, applyPoseToSkeleton } from "../rig/skeletonPose";
import { GARMENT_COVERAGE } from "../ml/garmentCoverage";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";

const DEFAULT_THRESHOLD = 0.3;

const GARMENT_RULES = {
  top: {
  wrapX: 0.03,
  widthBoost: 0.95,
  yShift: 0.0,
  heightBoost: 0.74,
  feather: 0.20,
  alphaTest: 0.20,
  zShift: 0.0,
},

bottom: {
  wrapX: 0.08,        // was 0.14 (less wrap = less horizontal overreach)
  widthBoost: 0.82,   // was 1.0  (MAIN FIX: shrink projection to match mesh)
  yShift: 0.1,       // tiny lift so crotch/hips don’t sag visually
  heightBoost: 0.85,  // was 0.95 (slightly shorter so hems don’t balloon)
  feather: 0.16,      // slightly cleaner edge
  alphaTest: 0.22,    // keep similar cutoff
  zShift: 0.0,
},

dress: {
  wrapX: 0.08,
  widthBoost: 1.14,
  yShift: 0.01,
  heightBoost: 1.02,
  feather: 0.11,
  alphaTest: 0.16,
  zShift: 0.0,
},

outer: {
  wrapX: 0.03,
  widthBoost: 1.37,
  yShift: -0.075,
  heightBoost: 1.06,
  feather: 0.10,
  alphaTest: 0.05,
  zShift: 0.0,
},

};

function ensureSRGB(tex) {
  if (!tex) return tex;

  tex.colorSpace = THREE.SRGBColorSpace;
  tex.flipY = false;

  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;

  // Safe default for projection mode.
  // Atlas mode can override this later if needed.
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;

  tex.needsUpdate = true;
  return tex;
}


function normalizeGarmentType(t) {
  const s = String(t || "").toLowerCase();

  if (s === "top" || s === "bottom" || s === "dress" || s === "outer") return s;

  if (s === "pants" || s === "jeans" || s === "trousers" || s === "skirt") return "bottom";
  if (s === "jacket" || s === "coat" || s === "blazer" || s === "cardigan") return "outer";

  console.warn("[GarmentMesh] Unknown garmentType:", t, "defaulting to 'top'");
  return "top";
}

function makeLoadingDotsMaterial() {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
    },
    vertexShader: `
      varying vec3 vPos;
      void main() {
        vPos = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec3 vPos;
      uniform float uTime;

      // cheap hash
      float hash(vec3 p) {
        p = fract(p * 0.3183099 + vec3(0.1,0.2,0.3));
        p *= 17.0;
        return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
      }

      void main() {
        // scale controls dot density
        vec3 gp = floor(vPos * 35.0);
        float n = hash(gp);

        // blinking dot mask
        float t = fract(n + uTime * 0.9);
       float dot = smoothstep(0.94, 1.0, t);


        // soften a bit so it’s not harsh
        float pulse = 0.25 + 0.75 * dot;

        
vec3 hi = vec3(0.95, 0.84, 0.71); // rose-gold dots

float alpha = smoothstep(0.2, 1.0, pulse);
if (alpha < 0.01) discard;       // kill background completely

gl_FragColor = vec4(hi, alpha);
      }
    `,
    transparent: false,
    depthTest: true,
    depthWrite: true,
    side: THREE.DoubleSide,
  });

  return mat;
}


/**
 * Two-sided planar project material (front+back in ONE shader) with side blend.
 * - Uses local space vPosLocal for mapping
 * - front/back chosen by sign of local axis (z by default)
 * - blend region around the seam controlled by uBlendWidth
 */
function makeSingleProjectMaterial(frontTex, backTex, axis = "z") {
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      mapFront: { value: frontTex },
      mapBack: { value: backTex },

      uMin: { value: new THREE.Vector3(-0.5, -0.5, -0.5) },
      uMax: { value: new THREE.Vector3(0.5, 0.5, 0.5) },
      uAxis: { value: axis === "x" ? 0 : axis === "y" ? 1 : 2 },

      uWrapX: { value: 0.30 },
      uWidthBoost: { value: 1.0 },
      uYShift: { value: 0.0 },
      uHeightBoost: { value: 1.0 },
      uFeather: { value: 0.09 },
      uFeatherYScale: { value: 1.0 },

      uDebug: { value: 0.0 },          // 0 normal, 1 projection UVs, 2 alpha
uDebugMeshUV: { value: 0.0 },
uUseMeshUVMapping: { value: 0.0 },
uNoFlipY: { value: 1.0 },

// Baked atlas UV normalisation.
// Needed for GLBs whose UVs are not inside 0..1.
uUseUvAtlasRange: { value: 0.0 },
uUvMin: { value: new THREE.Vector2(0, 0) },
uUvMax: { value: new THREE.Vector2(1, 1) },
      // how wide the side blend band is (in normalized local axis space)
      uBlendWidth: { value: 0.18 },

      uAlphaTest: { value: 0.02 },
      uAlphaFloor: { value: 0.0 },
      uAlphaGain: { value: 1.35 },

      uClampUV: { value: 1.0 },
      uUseExtractedAlpha: { value: 1.0 },

      // front/back flip (keeps your earlier orientation)
      uFlipXFront: { value: 1.0 },
      uFlipXBack: { value: 1.0 },
            // --- reveal scan (loading -> generated look)
      uTime: { value: 0.0 },
      uReveal: { value: 0.0 },           // 0..1
      uRevealActive: { value: 0.0 },     // 0/1
      uScanSoft: { value: 0.06 },        // softness of edge (0.02..0.12)
      uScanWidth: { value: 0.025 },      // bright band thickness (0.01..0.06)
      uColorGain: { value: 1.25 },   // start 1.15–1.40
uShadowLift: { value: 0.04 },  // start 0.02–0.08
uGamma: { value: 0.85 },      // < 1.0 brightens mids
uLift: { value: 0.08 },       // adds light to shadows
uEdgeDarken: { value: 0.10 },
uLightDir:  { value: new THREE.Vector3(-0.3, 0.8, 0.5) },
uLightWrap: { value: 0.35 },

// UV-based fabric shading
uUseUVShading: { value: 0.0 },
uUVShadeStrength: { value: 0.0 },
uUVShadeScale: { value: 18.0 },

uClipY: { value: 0.0 },
uClipMode: { value: 0.0 },   // 0 none, 1 hideAbove, 2 hideBelow
      mapOccFront: { value: null },
      mapOccBack: { value: null },
      uUseOccluder: { value: 0.0 },
      uOccMin: { value: new THREE.Vector3() },
      uOccWrapX: { value: 0.0 },
      uOccWidthBoost: { value: 1.0 },
      uOccHeightBoost: { value: 1.0 },
      uOccYShift: { value: 0.0 },
      uOccMax: { value: new THREE.Vector3() },

      // rose gold (RGB 0..1)
      uRoseBase: { value: new THREE.Color("#c9a16d") },
      uRoseHi: { value: new THREE.Color("#f2d7b6") },

    },

vertexShader: `
  #include <common>
  #include <uv_pars_vertex>
  #include <skinning_pars_vertex>
  #include <morphtarget_pars_vertex>
  #include <normal_pars_vertex>

  varying vec3 vPosLocal;
  varying vec2 vUv0;
  varying vec3 vNWorld;

  void main() {
    // ----- position (morph + skin) -----
    vec3 transformed = position;

    #include <morphtarget_vertex>
    #include <skinbase_vertex>
    #include <skinning_vertex>

    // ----- normal (morph + skin) -----
    #include <beginnormal_vertex>
    #include <morphnormal_vertex>
    #include <skinnormal_vertex>

    // projection coords after morph + skin
    vPosLocal = transformed;
    vUv0 = uv;

    // ✅ proper world normal (after skinnormal)
    vNWorld = normalize(normalMatrix * objectNormal);

    gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
  }
`,


fragmentShader: `
  uniform sampler2D mapFront;
  uniform sampler2D mapBack;

  uniform vec3 uMin;
  uniform vec3 uMax;
  uniform int uAxis;

  uniform float uWrapX;
  uniform float uWidthBoost;
  uniform float uYShift;
  uniform float uHeightBoost;
  uniform float uFeather;
  uniform float uFeatherYScale;

uniform float uDebug;
uniform float uDebugMeshUV;
uniform float uUseMeshUVMapping;
uniform float uNoFlipY;

uniform float uUseUvAtlasRange;
uniform vec2 uUvMin;
uniform vec2 uUvMax;

  uniform float uBlendWidth;

  uniform float uAlphaTest;
  uniform float uAlphaFloor;
  uniform float uAlphaGain;
  uniform float uClampUV;
  uniform float uUseExtractedAlpha;

  uniform float uFlipXFront;
  uniform float uFlipXBack;

  uniform float uTime;
  uniform float uReveal;
  uniform float uRevealActive;
  uniform float uScanSoft;
  uniform float uScanWidth;
  uniform vec3  uRoseBase;
  uniform vec3  uRoseHi;

  uniform float uColorGain;
  uniform float uShadowLift;
  uniform float uGamma;
  uniform float uLift;
  uniform vec3  uLightDir;
  uniform float uLightWrap;
 uniform float uEdgeDarken;

uniform float uUseUVShading;
uniform float uUVShadeStrength;
uniform float uUVShadeScale;

uniform float uClipY;
uniform float uClipMode;
uniform sampler2D mapOccFront;
uniform sampler2D mapOccBack;
uniform float uUseOccluder;
uniform vec3 uOccMin;
uniform vec3 uOccMax;
uniform float uOccWrapX;
uniform float uOccWidthBoost;
uniform float uOccHeightBoost;
uniform float uOccYShift;

  varying vec3 vPosLocal;
  varying vec2 vUv0;
  varying vec3 vNWorld;

  vec3 srgbToLinear(vec3 c) {
  return pow(c, vec3(2.2));
}
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float uvFabricNoise(vec2 uv) {
  // UV-space animation: this moves through the garment's UV map,
  // so it follows the mesh surface rather than world/projected space.
  vec2 animatedUV = uv + vec2(uTime * 0.015, uTime * 0.006);

  vec2 g = floor(animatedUV * uUVShadeScale);
  float n = hash21(g);

  return mix(0.94, 1.06, n);
}

  vec2 projUV(vec3 p) {
    vec3 t = (p - uMin) / (uMax - uMin);
    if (uAxis == 0) return vec2(t.z, t.y);
    if (uAxis == 1) return vec2(t.x, t.z);
    return vec2(t.x, t.y);
  }
  vec2 projOccUV(vec3 p) {
    vec3 t = (p - uOccMin) / (uOccMax - uOccMin);
    if (uAxis == 0) return vec2(t.z, t.y);
    if (uAxis == 1) return vec2(t.x, t.z);
    return vec2(t.x, t.y);
  }

  float axis01(vec3 p) {
    vec3 t = (p - uMin) / (uMax - uMin);
    if (uAxis == 0) return t.x;
    if (uAxis == 1) return t.y;
    return t.z;
  }

  vec4 sampleTex(sampler2D tex, vec2 uv, float flipX) {
    vec2 u = uv;

    // your current mapping conventions
    u.y = u.y + uYShift;
    u.y = 1.0 - u.y;

    if (flipX > 0.5) u.x = 1.0 - u.x;

    // wrap illusion
    u.x = (u.x - 0.5) * (1.0 - uWrapX) * uWidthBoost + 0.5;
    u.y = (u.y - 0.5) * (1.0 / uHeightBoost) + 0.5;

    if (uClampUV > 0.5) {
      float m = 0.002;
      u = clamp(u, vec2(m), vec2(1.0 - m));
    }

    vec4 c = texture2D(tex, u);

    float a = c.a;
    if (uUseExtractedAlpha < 0.5) a = 1.0;

    // hard cut
    if (a < uAlphaTest) return vec4(c.rgb, 0.0);

// soften edge (screen-stable AA)
float w = fwidth(a) * 1.25; // AA width in alpha space
a = smoothstep(uAlphaTest, uAlphaTest + max(uFeather, w), a);

// gain/floor
a = clamp(a * uAlphaGain, 0.0, 1.0);
a = max(a, uAlphaFloor);

// --- edge decontamination (kills dark halo) ---
// If RGB is contaminated outside the mask, "un-premultiply-ish" clean fixes the rim.
vec3 rgbClean = clamp(c.rgb / max(a, 1e-4), 0.0, 1.0);

// If you have linear workflow enabled, convert here; otherwise return rgbClean directly.
return vec4(rgbClean, a);
  }

    float sampleOccluderAlpha(sampler2D tex, vec2 uv, float flipX) {
    vec2 u = uv;

    u.y = u.y + uOccYShift;
    u.y = 1.0 - u.y;

    if (flipX > 0.5) u.x = 1.0 - u.x;

    u.x = (u.x - 0.5) * (1.0 - uOccWrapX) * uOccWidthBoost + 0.5;
    u.y = (u.y - 0.5) * (1.0 / uOccHeightBoost) + 0.5;

    if (uClampUV > 0.5) {
      float m = 0.002;
      u = clamp(u, vec2(m), vec2(1.0 - m));
    }

    vec4 c = texture2D(tex, u);
    return c.a;
  }

  void main() {
    vec2 projectedUV = projUV(vPosLocal);
vec2 meshUV = vUv0;

if (uUseUvAtlasRange > 0.5) {
  vec2 uvSize = max(uUvMax - uUvMin, vec2(0.000001));
  meshUV = (meshUV - uUvMin) / uvSize;
}

    // Debug actual GLB UVs.
    // Red = U, Green = V.
    // If the garment shows a smooth red/green layout, the mesh has usable UVs.
    // If it is mostly black/flat/chaotic, the UVs are missing or poor.
   if (uDebugMeshUV > 0.5) {
  vec2 uv = vUv0;

  float checks = mod(
    floor(uv.x * 16.0) + floor(uv.y * 16.0),
    2.0
  );

  vec3 colorA = vec3(1.0, 0.0, 1.0); // pink
  vec3 colorB = vec3(0.0, 1.0, 1.0); // cyan

  gl_FragColor = vec4(mix(colorA, colorB, checks), 1.0);
  return;
}
// Clothing image mapping.
// Default: projected front/back image placement.
// Test mode: true GLB UV mapping.
vec2 uv = projectedUV;

if (uUseMeshUVMapping > 0.5) {
  uv = meshUV;
}

if (uDebug > 0.5 && uDebug < 1.5) {
  gl_FragColor = vec4(fract(projectedUV), 0.0, 1.0);
  return;
}

    // seam blending factor
    float t = axis01(vPosLocal);          // 0..1
    float d = abs(t - 0.5) * 2.0;         // 0 seam, 1 extremes

    // local Y 0..1
    float y01 = clamp((vPosLocal.y - uMin.y) / (uMax.y - uMin.y), 0.0, 1.0);

    float shoulder = smoothstep(0.70, 0.93, y01);

    float wBase = clamp(uBlendWidth, 0.001, 0.49);
    float wShoulder = min(0.46, wBase * 2.8);
    float w = mix(wBase, wShoulder, shoulder);

    float seam = smoothstep(0.0, w, d);

    vec4 cf = sampleTex(mapFront, uv, uFlipXFront);
    vec4 cb = sampleTex(mapBack,  uv, uFlipXBack);

    float bias = 0.06 * shoulder;
    float isFrontSoft = smoothstep(0.5 - bias, 0.5 + bias, t);

    vec4 cPick = mix(cb, cf, isFrontSoft);
    vec4 cBlend = mix(mix(cb, cf, 0.5), cPick, seam);
    cBlend = mix(cBlend, cf, shoulder * 0.06);

    if (uDebug > 1.5) {
      gl_FragColor = vec4(vec3(cBlend.a), 1.0);
      return;
    }

    float a = cBlend.a;
    if (a < uAlphaTest) discard;

        // -----------------------
    // Occluder mask clipping
    // -----------------------
if (uUseOccluder > 0.5) {
  vec3 occSize = uOccMax - uOccMin;

  if (occSize.x >= 1e-5 && occSize.y >= 1e-5 && occSize.z >= 1e-5) {
    vec2 occUV = projOccUV(vPosLocal);

    float occAFront = sampleOccluderAlpha(mapOccFront, occUV, uFlipXFront);
    float occABack  = sampleOccluderAlpha(mapOccBack,  occUV, uFlipXBack);
    float occA = max(occAFront, occABack);

    if (occA > 0.2) discard;
  }
}

    // -----------------------
    // Overlap clipping
    // -----------------------
    if (uUseOccluder < 0.5 && uClipMode > 0.5) {
      float y = vPosLocal.y;

      if (uClipMode < 1.5) {
        // hide ABOVE line
        if (y > uClipY) discard;
      } else {
        // hide BELOW line
        if (y < uClipY) discard;
      }
    }

    // Reveal scan
    if (uRevealActive > 0.5) {
      float y01r = clamp((vPosLocal.y - uMin.y) / (uMax.y - uMin.y), 0.0, 1.0);
      float p = clamp(uReveal, 0.0, 1.0);
      float edge = 1.0 - p;

      float revealMask = smoothstep(edge, edge + uScanSoft, y01r);
      float band = 1.0 - smoothstep(0.0, uScanWidth, abs(y01r - edge));
      float shimmer = 0.6 + 0.4 * sin(uTime * 6.0 + y01r * 20.0);

      vec3 rose = mix(uRoseBase, uRoseHi, clamp(band * shimmer, 0.0, 1.0));
      vec3 rgbR = mix(rose, cBlend.rgb, revealMask);
      float outA = a * max(revealMask, 0.15);

      gl_FragColor = vec4(rgbR, outA);
      return;
    }

    vec3 rgb = cBlend.rgb;

    // lighting using real normal
    vec3 N = normalize(vNWorld);
    vec3 L = normalize(uLightDir);
    float ndotl = clamp(dot(N, L), 0.0, 1.0);

    float wrapped = mix(ndotl, 1.0 - (1.0 - ndotl) * (1.0 - ndotl), uLightWrap);
    float ambient = 0.35;
    float lit = clamp(ambient + (1.0 - ambient) * wrapped, 0.0, 1.0);
    rgb *= mix(1.0, lit, 0.35);  // only 35% lighting influence

    // grading
rgb += vec3(uLift);
rgb *= uColorGain;
rgb = pow(max(rgb, 0.0), vec3(uGamma));
rgb = clamp(rgb, 0.0, 1.0);

// UV-based fabric shading.
// Important: this uses meshUV, but the clothing image itself still uses projectedUV.
if (uUseUVShading > 0.5) {
  float fabric = uvFabricNoise(meshUV);
  rgb *= mix(1.0, fabric, uUVShadeStrength);
  rgb = clamp(rgb, 0.0, 1.0);
}

// subtle edge depth cue (no new seam logic): darken only where alpha is feathering
float edge = 1.0 - smoothstep(0.85, 1.0, a);
rgb *= (1.0 - edge * uEdgeDarken); 

gl_FragColor = vec4(rgb, a);
  }
`,


transparent: true,     // ✅ treat as cutout, not blended transparency
depthTest: true,
depthWrite: true,       // ✅ writes depth so mannequin can’t leak through
side: THREE.DoubleSide,
alphaTest: 0.001,       // (optional) makes Three treat it like cutout internally

  });

  mat.morphTargets = true;

// IMPORTANT: force shader recompile with skinning defines
mat.skinning = true;
mat.needsUpdate = true;

return mat;

}

export default function GarmentMesh({
  glbPath,
  frontTexture = null,
  backTexture = null,
  fillColor = "#b8b8b8",
  normalZThreshold = DEFAULT_THRESHOLD,
  roughnessFront = 0.85,
  roughnessBack = 0.85,
  roughnessSide = 0.95,
  metalness = 0.0,
  castShadow = true,
  receiveShadow = false,
  garmentType = "top",
  rulesOverride = null,
  avatarParams = null,
  poseSpec = null,
  isLoading = false,
  projectionFit = null, // ✅ only affects projection mapping (uMin/uMax)
  onBounds = null,
  overlapMode = null,   // "tucked" | "untucked" | null
  clipY = null,         // world/local Y to clip against
  clipTarget = null, 
  occluderFrontSrc = null,
  occluderBackSrc = null,
  occluderMin = null,
  occluderMax = null,
  occluderCategory = null,
  occluderFitData = null,
  stencilMode = "none",     // "none" | "occluderMask" | "rejectOccluder"
    disableClip = false,
  debugMeshUV = false,
useMeshUVMapping = false,
uvAtlasRange = null,
}) {
  const safePath =
    typeof glbPath === "string" && glbPath.length ? glbPath : "/__missing__.glb";

  const { scene: gltfScene } = useGLTF(safePath);

  useEffect(() => {
  const isCloudflare =
    typeof safePath === "string" &&
    (safePath.includes("r2.dev") || safePath.includes("cloudflare"));

  console.log("[GarmentMesh] ACTUAL GLB SOURCE", {
    safePath,
    isCloudflare,
    isLocalPublicFile: typeof safePath === "string" && safePath.startsWith("/"),
  });
}, [safePath]);


  useEffect(() => {
  if (!gltfScene) return;

  const rows = [];
  gltfScene.traverse?.((o) => {
    rows.push({
      path: safePath,
      name: o.name,
      type: o.type,
      isMesh: !!o.isMesh,
      isSkinnedMesh: !!o.isSkinnedMesh,
      hasGeometry: !!o.geometry,
      vertexCount: o.geometry?.attributes?.position?.count ?? null,
      hasSkeleton: !!o.skeleton,
      morphKeys: o.morphTargetDictionary ? Object.keys(o.morphTargetDictionary) : [],
    });
  });

  console.table(rows);
}, [gltfScene, safePath]);

const scene = useMemo(() => cloneSkeleton(gltfScene), [gltfScene]);

useEffect(() => {
  if (!scene) return;
  cacheRestPose(scene);
}, [scene]);

  const groupRef = useRef(null);

  // ---------------------------
  // Reveal scan animation state
  // ---------------------------
  const revealRef = useRef({
    active: false,
    t: 0,
    duration: 1.2, // seconds (slow fade)
  });

    // ---------------------------
  // Loading animation material
  // ---------------------------
  const loadingMat = useMemo(() => makeLoadingDotsMaterial(), []);

  useEffect(() => {
    return () => loadingMat?.dispose?.();
  }, [loadingMat]);

  useFrame((_, delta) => {
    if (!loadingMat?.uniforms?.uTime) return;
    loadingMat.uniforms.uTime.value += delta;
  });


  const frontTex = useMemo(() => ensureSRGB(frontTexture), [frontTexture]);
  const backTex = useMemo(() => ensureSRGB(backTexture), [backTexture]);

    const EMPTY_PNG =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=";

  const occFrontRaw = occluderFrontSrc || EMPTY_PNG;
  const occBackRaw = occluderBackSrc || occluderFrontSrc || EMPTY_PNG;

  const occFrontTexLoaded = useTexture(occFrontRaw);
  const occBackTexLoaded = useTexture(occBackRaw);

  const occFrontTex = useMemo(() => ensureSRGB(occFrontTexLoaded), [occFrontTexLoaded]);
  const occBackTex = useMemo(() => ensureSRGB(occBackTexLoaded), [occBackTexLoaded]);

  // ✅ fallback back -> front so singleMat always has both
  const effectiveBackTex = backTex ?? frontTex;

  const typeKey = useMemo(() => normalizeGarmentType(garmentType), [garmentType]);

  const rules = useMemo(() => {
    const base = GARMENT_RULES[typeKey] || GARMENT_RULES.top;
    return rulesOverride ? { ...base, ...rulesOverride } : base;
  }, [typeKey, rulesOverride]);

    const occTypeKey = useMemo(
    () => normalizeGarmentType(occluderCategory),
    [occluderCategory]
  );

  const occRules = useMemo(() => {
    return GARMENT_RULES[occTypeKey] || GARMENT_RULES.top;
  }, [occTypeKey]);

  useEffect(() => {
    if (safePath === "/__missing__.glb") {
      console.error(
        "[GarmentMesh] Missing/invalid glbPath. You must pass glbPath='/models/foo.glb'.",
        { glbPath }
      );
    }
  }, [safePath, glbPath]);

const sourceMesh = useMemo(() => {
  if (safePath === "/__missing__.glb") return null;

  let foundSkinned = null;
  let foundPlain = null;

  scene?.traverse?.((o) => {
    if (!foundSkinned && o?.isSkinnedMesh && o?.geometry) {
      foundSkinned = o;
      return;
    }

    if (!foundPlain && o?.isMesh && o?.geometry) {
      foundPlain = o;
    }
  });

  return foundSkinned || foundPlain || null;
}, [scene, safePath]);
 
 useEffect(() => {
  if (!sourceMesh) return;

  const geom = sourceMesh.geometry;
  const pos = geom?.attributes?.position;
  const uv = geom?.attributes?.uv;

  if (geom && !geom.boundingBox) geom.computeBoundingBox();

  console.log("[GarmentMesh] UV check", {
    meshPath: safePath,
    meshName: sourceMesh.name,
    hasUV: !!uv,
    uvCount: uv?.count ?? null,
    vertexCount: pos?.count ?? null,
    geometryUuid: geom?.uuid ?? null,
    boundingBox: geom?.boundingBox
      ? {
          min: geom.boundingBox.min.toArray(),
          max: geom.boundingBox.max.toArray(),
        }
      : null,
    firstUVs: uv
      ? [
          [uv.getX(0), uv.getY(0)],
          [uv.getX(1), uv.getY(1)],
          [uv.getX(2), uv.getY(2)],
          [uv.getX(3), uv.getY(3)],
          [uv.getX(4), uv.getY(4)],
        ]
      : null,
  });
}, [sourceMesh, safePath]);

// We clone the hierarchy that owns the skeleton (NOT just the SkinnedMesh),
// so skinning/morph targets remain intact on the render clone.
const renderRoot = useMemo(() => {
if (!sourceMesh) {
  console.log("[GARMENT RENDER ROOT]", {
    safePath,
    hasSourceMesh: false,
    sourceMeshName: null,
    sourceMeshType: null,
    rootName: null,
    rootType: null,
    isSkinned: null,
    usedParentClone: null,
  });
  return null;
}

let root;

if (sourceMesh.isSkinnedMesh) {
  // ✅ keep existing behaviour for rigged garments
  root = sourceMesh.parent
    ? cloneSkeleton(sourceMesh.parent)
    : cloneSkeleton(sourceMesh);
} else {
  // ✅ FIX: plain mesh → clone ONLY the mesh (no parent chain)
  root = sourceMesh.clone();

  // ensure clean transform baseline (prevents inherited offsets)
  root.position.set(0, 0, 0);
  root.rotation.set(0, 0, 0);
  root.scale.set(1, 1, 1);
}

  root.traverse?.((o) => {
    if (o?.isSkinnedMesh) o.frustumCulled = false;
  });

  console.log("[GARMENT RENDER ROOT]", {
    safePath,
    hasSourceMesh: true,
    sourceMeshName: sourceMesh.name || null,
    sourceMeshType: sourceMesh.type || null,
    rootName: root?.name || null,
    rootType: root?.type || null,
  });

  return root;
}, [sourceMesh, safePath]);


 useEffect(() => {
  if (!renderRoot) return;
  if (!groupRef.current) return;

  const parent = groupRef.current;

  // Reset local transforms on the root we attach
  renderRoot.position.set(0, 0, 0);
  renderRoot.rotation.set(0, 0, 0);
  renderRoot.scale.set(1, 1, 1);
  renderRoot.updateMatrix();
  renderRoot.updateMatrixWorld(true);

  renderRoot.traverse?.((o) => {
  if (!o?.isSkinnedMesh || !o?.skeleton) return;

  // keep correct mode but DO NOT rebind
  o.bindMode = THREE.AttachedBindMode;

  // just update skeleton
  o.skeleton.update();
});

parent.add(renderRoot);

// ✅ IMPORTANT: update world matrices now that it's parented
parent.updateMatrixWorld(true);
renderRoot.updateMatrixWorld(true);

  return () => {
    try {
      parent.remove(renderRoot);
    } catch {}
  };
}, [renderRoot]);

  // ✅ APPLY POSE (skeleton) TO GARMENT so it matches mannequin pose
useEffect(() => {
  if (!renderRoot) return;

  applyPoseToSkeleton(renderRoot, poseSpec, { reset: true });
}, [renderRoot, poseSpec]);

// ✅ APPLY AVATAR MORPH TARGETS TO GARMENT (ALL skinned meshes)
useEffect(() => {
  if (!renderRoot) return;

  const v = {
    Arm_up: Number(avatarParams?.Arm_up ?? 0),
    Bust_up: Number(avatarParams?.Bust_up ?? 0),
    Waist_up: Number(avatarParams?.Waist_up ?? 0),
    Hip_up: Number(avatarParams?.Hip_up ?? 0),
    Leg_up: Number(avatarParams?.Leg_up ?? 0),
  };

  let appliedCount = 0;

renderRoot.traverse?.((o) => {
  if (!o?.isSkinnedMesh) return;

  const dict = o.morphTargetDictionary;
  const inf = o.morphTargetInfluences;
  if (!dict || !inf) return;

  for (const key of Object.keys(v)) {
    const idx = dict[key];
    if (idx !== undefined) {
      inf[idx] = Math.max(0, Math.min(1, v[key]));
    }
  }

  appliedCount++;
});

  console.log("[GarmentMesh] morph applied to garment meshes:", appliedCount, v);
}, [
  renderRoot,
  avatarParams?.Arm_up,
  avatarParams?.Bust_up,
  avatarParams?.Waist_up,
  avatarParams?.Hip_up,
  avatarParams?.Leg_up,
]);

useEffect(() => {
  if (!renderRoot) return;

  renderRoot.traverse?.((o) => {
    if (!o?.isMesh) return;

    console.log("[GarmentMesh] mesh morph audit", {
      name: o.name,
      isSkinnedMesh: !!o.isSkinnedMesh,
      hasMorphDict: !!o.morphTargetDictionary,
      morphKeys: o.morphTargetDictionary ? Object.keys(o.morphTargetDictionary) : [],
      influenceCount: o.morphTargetInfluences?.length ?? 0,
      materialMorphTargets: !!o.material?.morphTargets,
    });
  });
}, [renderRoot]);


  useEffect(() => {
    if (!sourceMesh) return;
    const dict = sourceMesh.morphTargetDictionary;
    const inf = sourceMesh.morphTargetInfluences;
    const morphPos = sourceMesh.geometry?.morphAttributes?.position;
    console.log("[GarmentMesh] morphTargets?", {
      hasDict: !!dict,
      keys: dict ? Object.keys(dict) : [],
      influencesLen: inf?.length ?? 0,
      morphPosLen: morphPos ? morphPos.length : 0,
    });
  }, [sourceMesh]);

  const singleMat = useMemo(() => {
    if (!frontTex) return null;
    if (!effectiveBackTex) return null;
    return makeSingleProjectMaterial(frontTex, effectiveBackTex, "z");
  }, [frontTex, effectiveBackTex]);

    // ---------------------------
  // Start/stop reveal based on loading
  // ---------------------------
  useEffect(() => {
    if (!singleMat?.uniforms) return;

    const U = singleMat.uniforms;

    // While loading: show scan, progress = 0
    if (isLoading) {
      revealRef.current.active = false;
      revealRef.current.t = 0;
      if (U.uRevealActive) U.uRevealActive.value = 1.0;
      if (U.uReveal) U.uReveal.value = 0.0;
      return;
    }

    // When loading finishes: kick off reveal animation
    revealRef.current.active = true;
    revealRef.current.t = 0;
    if (U.uRevealActive) U.uRevealActive.value = 1.0;
    if (U.uReveal) U.uReveal.value = 0.0;
  }, [isLoading, singleMat]);


useEffect(() => {
  if (!renderRoot) return;

  renderRoot.traverse?.((o) => {
    if (!o?.isMesh) return;

 const mat = isLoading ? loadingMat : singleMat;

    o.material = mat;
    o.castShadow = castShadow;
    o.receiveShadow = receiveShadow;
    o.frustumCulled = false;

    // reset defaults first
    mat.colorWrite = true;
    mat.depthTest = true;
    mat.depthWrite = true;

    mat.stencilWrite = false;
    mat.stencilRef = 1;
    mat.stencilFunc = THREE.AlwaysStencilFunc;
    mat.stencilFail = THREE.KeepStencilOp;
    mat.stencilZFail = THREE.KeepStencilOp;
    mat.stencilZPass = THREE.KeepStencilOp;

    if (stencilMode === "occluderMask") {
      // Invisible silhouette pass: only mark stencil where garment alpha actually draws
      mat.colorWrite = false;
      mat.depthTest = true;
      mat.depthWrite = false;

      mat.stencilWrite = true;
      mat.stencilRef = 1;
      mat.stencilFunc = THREE.AlwaysStencilFunc;
      mat.stencilFail = THREE.KeepStencilOp;
      mat.stencilZFail = THREE.KeepStencilOp;
      mat.stencilZPass = THREE.ReplaceStencilOp;
    }

    if (stencilMode === "rejectOccluder") {
      // Normal pants draw, but reject where stencil already contains occluder silhouette
      mat.colorWrite = true;
      mat.depthTest = true;
      mat.depthWrite = true;

      mat.stencilWrite = false;
      mat.stencilRef = 1;
      mat.stencilFunc = THREE.NotEqualStencilFunc;
      mat.stencilFail = THREE.KeepStencilOp;
      mat.stencilZFail = THREE.KeepStencilOp;
      mat.stencilZPass = THREE.KeepStencilOp;
    }

    mat.needsUpdate = true;
  });
}, [
  renderRoot,
  singleMat,
  loadingMat,
  isLoading,
  castShadow,
  receiveShadow,
  stencilMode,
  disableClip,
]);

  // ---------------------------
  // Per-frame reveal animation
  // ---------------------------
  useFrame((_, delta) => {
    if (!singleMat?.uniforms) return;

    const U = singleMat.uniforms;

    // Always tick time (used for shimmer in scan band)
    if (U.uTime) U.uTime.value += delta;

    // Animate reveal if active
    if (revealRef.current.active) {
      revealRef.current.t += delta;
      const d = Math.max(0.1, revealRef.current.duration);
      const p = Math.min(1, revealRef.current.t / d);

      if (U.uReveal) U.uReveal.value = p;

      // When done: disable special path
      if (p >= 1) {
        revealRef.current.active = false;
        if (U.uRevealActive) U.uRevealActive.value = 0.0;
      }
    }
  });

useEffect(() => {
  if (!renderRoot) return;

  const U = singleMat?.uniforms || null;
 let m = null;
renderRoot.traverse?.((o) => {
  if (!m && o?.isMesh && o?.geometry) m = o;
});
const geom = m?.geometry;

console.log("[GarmentMesh] bounds mesh", {
  safePath,
  typeKey,
  foundMesh: !!m,
  meshName: m?.name || null,
  isSkinnedMesh: !!m?.isSkinnedMesh,
  hasGeometry: !!geom,
  vertexCount: geom?.attributes?.position?.count ?? 0,
});

  // ✅ stable LOCAL bounds from geometry (not world transforms)
  if (!geom.boundingBox) geom.computeBoundingBox();
  const bb = geom.boundingBox;
  if (!bb) return;

    let minP = bb.min.clone();
  let maxP = bb.max.clone();
  

  // report measured bounds to parent (local units)
  if (typeof onBounds === "function") {
    onBounds({
      type: typeKey,
      min: minP.clone(),
      max: maxP.clone(),
      size: new THREE.Vector3(
        maxP.x - minP.x,
        maxP.y - minP.y,
        maxP.z - minP.z
      ),
    });
  }

  // -----------------------------
  // Category-specific padding (mesh-space)
  // -----------------------------
  if (typeKey === "outer") {
    const h = maxP.y - minP.y;
    minP.y -= h * 0.28;
    maxP.y += h * 0.09;
  }

  if (typeKey === "bottom") {
  const h = maxP.y - minP.y;

  // Expand slightly so waistband + hems don't get clipped.
  // IMPORTANT: symmetric so it doesn't shift the mapping center.
  const pad = h * 0.02;
  minP.y -= pad;
  maxP.y += pad;
}

  // -----------------------------
  // ✅ Projection fit (virtual box only)
  // Mesh stays untouched.
  // -----------------------------
  const pf = projectionFit || {};

let sX = Number(pf.scaleX ?? 1);
let sY = Number(pf.scaleY ?? 1);
let sZ = Number(pf.scaleZ ?? 1);
let yOff = Number(pf.yOffset ?? 0);


if (typeKey === "bottom") {
  const fitKey = String(projectionFit?.key || "");
  const isShortsLike =
    fitKey === "bottom:shorts" ||
    fitKey === "bottom:shorts_mini" ||
    fitKey === "bottom:bermuda";

  if (!isShortsLike) {
    yOff = THREE.MathUtils.clamp(yOff, -0.08, 0.14);
  } else {
    yOff = THREE.MathUtils.clamp(yOff, -0.35, 0.45);
  }

  sX = THREE.MathUtils.clamp(sX, 0.78, 1.0);
}
const boxH = Math.max(1e-6, (maxP.y - minP.y));
if (typeKey === "dress") {
  // ✅ dresses were unbounded — this is why only a strip shows
  yOff = THREE.MathUtils.clamp(yOff, -0.10 * boxH, 0.18 * boxH);

  // keep projection scaling sane (illusion only)
  sX = THREE.MathUtils.clamp(sX, 0.90, 1.18);
}
const baseType = String(typeKey).split(":")[0]; // "outer:closed" -> "outer"
if (baseType === "outer") {
  // Outerwear: modest room, never balloon.
  sX  = THREE.MathUtils.clamp(sX, 0.85, 1.10);
  sY  = THREE.MathUtils.clamp(sY, 0.85, 1.10);

  // Let it drop a bit, but never a full torso-length.
  yOff = THREE.MathUtils.clamp(yOff, -0.18 * boxH, 0.06 * boxH);
}

  // scale box around its center
  const c = new THREE.Vector3().addVectors(minP, maxP).multiplyScalar(0.5);
  const half = new THREE.Vector3().subVectors(maxP, minP).multiplyScalar(0.5);

  half.x *= sX;
  half.y *= sY;
  half.z *= sZ;

  // shift mapping box vertically
  c.y += yOff;

  const projMin = new THREE.Vector3(c.x - half.x, c.y - half.y, c.z - half.z);
  const projMax = new THREE.Vector3(c.x + half.x, c.y + half.y, c.z + half.z);

if (U?.uMin) U.uMin.value.copy(projMin);
if (U?.uMax) U.uMax.value.copy(projMax);

if (!U) return;

if (U.uDebugMeshUV) {
  U.uDebugMeshUV.value = debugMeshUV ? 1.0 : 0.0;
}

// Make sure the old projection debug is off while testing real mesh UVs.
if (U.uDebug && debugMeshUV) {
  U.uDebug.value = 0.0;
}

if (U.uUseMeshUVMapping) {
  U.uUseMeshUVMapping.value = useMeshUVMapping ? 1.0 : 0.0;
}

if (U.uUseUvAtlasRange) {
  const hasRange =
    !!uvAtlasRange &&
    Number.isFinite(uvAtlasRange.minX) &&
    Number.isFinite(uvAtlasRange.maxX) &&
    Number.isFinite(uvAtlasRange.minY) &&
    Number.isFinite(uvAtlasRange.maxY);

  U.uUseUvAtlasRange.value = useMeshUVMapping && hasRange ? 1.0 : 0.0;

  if (hasRange && U.uUvMin && U.uUvMax) {
    U.uUvMin.value.set(uvAtlasRange.minX, uvAtlasRange.minY);
    U.uUvMax.value.set(uvAtlasRange.maxX, uvAtlasRange.maxY);
  }
}

// -----------------------------
// Overlap clipping logic
// -----------------------------
if (U.uClipMode && U.uClipY) {
  let mode = 0;

  if (!disableClip && overlapMode && clipY !== null && clipTarget) {
    if (clipTarget === "bottom") {
      mode = 1;
    } else if (clipTarget === "top") {
      mode = 2;
    }
  }

  U.uClipMode.value = mode;
  U.uClipY.value = clipY ?? 0.0;
}

if (U.mapOccFront) U.mapOccFront.value = occFrontTex ?? null;
if (U.mapOccBack) U.mapOccBack.value = occBackTex ?? null;

if (U.uOccMin && U.uOccMax) {
  if (occluderMin && occluderMax) {
    let occMinP = occluderMin.clone();
    let occMaxP = occluderMax.clone();

    if (occTypeKey === "outer") {
      const h = occMaxP.y - occMinP.y;
      occMinP.y -= h * 0.28;
      occMaxP.y += h * 0.09;
    }

    if (occTypeKey === "bottom") {
      const h = occMaxP.y - occMinP.y;
      const pad = h * 0.02;
      occMinP.y -= pad;
      occMaxP.y += pad;
    }

    const occPf = occluderFitData || {};
    let osX = Number(occPf.scaleX ?? 1);
    let osY = Number(occPf.scaleY ?? 1);
    let osZ = Number(occPf.scaleZ ?? 1);
    let oyOff = Number(occPf.yOffset ?? 0);

    const occBoxH = Math.max(1e-6, occMaxP.y - occMinP.y);

    if (occTypeKey === "dress") {
      oyOff = THREE.MathUtils.clamp(oyOff, -0.10 * occBoxH, 0.18 * occBoxH);
      osX = THREE.MathUtils.clamp(osX, 0.90, 1.18);
    }

    const occBaseType = String(occTypeKey).split(":")[0];
    if (occBaseType === "outer") {
      osX = THREE.MathUtils.clamp(osX, 0.85, 1.10);
      osY = THREE.MathUtils.clamp(osY, 0.85, 1.10);
      oyOff = THREE.MathUtils.clamp(oyOff, -0.18 * occBoxH, 0.06 * occBoxH);
    }

    const occC = new THREE.Vector3().addVectors(occMinP, occMaxP).multiplyScalar(0.5);
    const occHalf = new THREE.Vector3().subVectors(occMaxP, occMinP).multiplyScalar(0.5);

    occHalf.x *= osX;
    occHalf.y *= osY;
    occHalf.z *= osZ;

    occC.y += oyOff;

    const occProjMin = new THREE.Vector3(
      occC.x - occHalf.x,
      occC.y - occHalf.y,
      occC.z - occHalf.z
    );

    const occProjMax = new THREE.Vector3(
      occC.x + occHalf.x,
      occC.y + occHalf.y,
      occC.z + occHalf.z
    );

    U.uOccMin.value.copy(occProjMin);
    U.uOccMax.value.copy(occProjMax);
  } else {
    U.uOccMin.value.set(0, 0, 0);
    U.uOccMax.value.set(0, 0, 0);
  }
}

if (U.uOccWrapX)       U.uOccWrapX.value       = Number(occRules.wrapX ?? 0.0);
if (U.uOccWidthBoost)  U.uOccWidthBoost.value  = Number(occRules.widthBoost ?? 1.0);
if (U.uOccHeightBoost) U.uOccHeightBoost.value = Number(occRules.heightBoost ?? 1.0);
if (U.uOccYShift)      U.uOccYShift.value      = Number(occRules.yShift ?? 0.0);

if (U.uUseOccluder) {
  if (stencilMode === "rejectOccluder" || stencilMode === "occluderMask") {
    U.uUseOccluder.value = 0.0;
  } else {
    U.uUseOccluder.value =
      (overlapMode === "untucked" || overlapMode === "outer") &&
      clipTarget === "bottom" &&
      !!occluderFrontSrc &&
      !!occluderMin &&
      !!occluderMax
        ? 1.0
        : 0.0;
  }
}

  // Projection box does the fit. These are only *illusion* controls.
if (U.uWrapX)      U.uWrapX.value      = Number(rules.wrapX ?? 0.0);
if (U.uWidthBoost) U.uWidthBoost.value = Number(rules.widthBoost ?? 1.0);
if (U.uYShift)     U.uYShift.value     = Number(rules.yShift ?? 0.0);
if (U.uHeightBoost)U.uHeightBoost.value= Number(rules.heightBoost ?? 1.0);

const hip = Number(avatarParams?.Hip_up ?? 0);
const leg = Number(avatarParams?.Leg_up ?? 0);
const curve = Math.max(hip, leg); // 0..1

if (typeKey === "bottom") {
  if (U.uWrapX) U.uWrapX.value += 0.05 * curve;
  if (U.uWidthBoost) U.uWidthBoost.value += 0.05 * curve;
} else if (typeKey === "top") {
  if (U.uWrapX) U.uWrapX.value += 0.02 * curve;
  if (U.uWidthBoost) U.uWidthBoost.value += 0.02 * curve;
} else if (typeKey === "dress") {
  if (U.uWrapX) U.uWrapX.value += 0.03 * curve;
  if (U.uWidthBoost) U.uWidthBoost.value += 0.06 * curve; // skirts need more
}

  if (U.uFeather) U.uFeather.value = rules.feather;
  if (U.uBlendWidth) {
  const base = Math.max(0.08, Math.min(0.35, (rules.feather ?? 0.1) * 1.8));

  // bottoms: slightly wider blend band so side edges feel wrapped
  U.uBlendWidth.value = (typeKey === "bottom")
    ? Math.min(0.42, base * 2.15)
    : base;
}
  
    if (U.uClampUV) U.uClampUV.value = 0.0; // IMPORTANT: stop clamping for UV mode
 // --- alpha policy ---
// Use extracted PNG alpha for ALL categories (including tops)
if (U.uUseExtractedAlpha) U.uUseExtractedAlpha.value = 1.0;

if (U.uAlphaGain) U.uAlphaGain.value = 1.0;
if (U.uAlphaFloor) U.uAlphaFloor.value = 0.0;

// Top needs a *real* cutoff to kill the outside junk (sleeves/hip band)
if (U.uAlphaTest) {
  if (typeKey === "top") U.uAlphaTest.value = 0.22;
  else if (typeKey === "dress") U.uAlphaTest.value = 0.16; // ✅ softer for armholes/cutouts
  else if (typeKey === "outer") U.uAlphaTest.value = 0.06;
  else U.uAlphaTest.value = (rules.alphaTest ?? 0.15);
}

// Reset UV shading by default.
// Category blocks can opt back in.
if (U.uUseUVShading) U.uUseUVShading.value = 0.0;
if (U.uUVShadeStrength) U.uUVShadeStrength.value = 0.06;
if (U.uUVShadeScale) U.uUVShadeScale.value = 28.0;

if (typeKey === "top") {
  // 1) flatten the lighting influence
  if (U.uLightWrap) U.uLightWrap.value = 0.15;  // much flatter
    if (U.uAlphaTest) U.uAlphaTest.value = 0.20;  // slightly lower than 0.22
  if (U.uFeather)   U.uFeather.value = 0.10; 
  // 2) remove grading so texture stays true
  if (U.uColorGain) U.uColorGain.value = 1.0;
  if (U.uGamma)     U.uGamma.value = 1.0;
  if (U.uLift)      U.uLift.value = 0.0;
}
// --- BOTTOM (pants) shading: add contrast, reduce washout ---
if (typeKey === "bottom") {
  // slightly stronger lighting influence than tops
  if (U.uLightWrap) U.uLightWrap.value = 0.28;

  // denim needs a touch more punch
  if (U.uColorGain) U.uColorGain.value = 0.95;
  if (U.uGamma)     U.uGamma.value = 0.85;
  if (U.uLift)      U.uLift.value = 0.0;

  // keep hems crisp
  if (U.uAlphaTest) U.uAlphaTest.value = 0.22;
  if (U.uFeather)   U.uFeather.value = 0.09;
  if (U.uEdgeDarken) U.uEdgeDarken.value = 0.04;

  // UV-based shading test
  if (U.uUseUVShading) U.uUseUVShading.value = 1.0;
  if (U.uUVShadeStrength) U.uUVShadeStrength.value = 0.12;
  if (U.uUVShadeScale) U.uUVShadeScale.value = 22.0;
}

// --- DRESS shading / edges ---
if (typeKey === "dress") {
  if (U.uLightWrap) U.uLightWrap.value = 0.20;

  if (U.uColorGain) U.uColorGain.value = 1.0;
  if (U.uGamma)     U.uGamma.value = 1.0;
  if (U.uLift)      U.uLift.value = 0.0;

  if (U.uAlphaTest) U.uAlphaTest.value = 0.16;
  if (U.uFeather)   U.uFeather.value = 0.11;
  if (U.uEdgeDarken) U.uEdgeDarken.value = 0.03;
}

// --- OUTERWEAR shading / edges ---
// Jackets/coats/puffers should stay closest to source colour.
// They need flatter lighting and much less grading than default.
if (typeKey === "outer") {
  // flatter, less fake wrap light
  if (U.uLightWrap) U.uLightWrap.value = 0.20;

  // keep original colour truer
if (U.uColorGain) U.uColorGain.value = 1.08;
if (U.uGamma)     U.uGamma.value = 0.90;
if (U.uLift)      U.uLift.value = 0.03;

  // cleaner cutout without making sleeves disappear
  if (U.uAlphaTest) U.uAlphaTest.value = 0.06;
  if (U.uFeather)   U.uFeather.value = 0.08;

  // reduce dark halo/depth cue on jacket edges
  if (U.uEdgeDarken) U.uEdgeDarken.value = 0.03;
}

  if (U.uFlipXFront) U.uFlipXFront.value = 0.0;
  if (U.uFlipXBack) U.uFlipXBack.value = 1.0;

  // -----------------------------
// UV image mapping test override
// -----------------------------
// Current product cutouts are not UV atlases, so their alpha will often
// sample transparent space and discard the whole garment.
// For UV-mapping tests, ignore extracted alpha so we can actually see
// where the image lands on the UV map.
if (useMeshUVMapping) {
  if (U.uUseExtractedAlpha) U.uUseExtractedAlpha.value = 0.0;
  if (U.uAlphaTest) U.uAlphaTest.value = 0.0;
  if (U.uAlphaGain) U.uAlphaGain.value = 1.0;
  if (U.uAlphaFloor) U.uAlphaFloor.value = 1.0;

  // IMPORTANT:
  // The pants GLB has UVs outside 0..1.
  // Do NOT clamp in atlas mode, or the shader mostly samples texture edges.
  if (U.uClampUV) U.uClampUV.value = 1.0;

  // No front/back flips for baked UV atlas mode.
  if (U.uFlipXFront) U.uFlipXFront.value = 0.0;
  if (U.uFlipXBack) U.uFlipXBack.value = 0.0;

  if (frontTex) {
  frontTex.wrapS = THREE.ClampToEdgeWrapping;
frontTex.wrapT = THREE.ClampToEdgeWrapping;
  frontTex.needsUpdate = true;
}

if (backTex) {
  backTex.wrapS = THREE.ClampToEdgeWrapping;
backTex.wrapT = THREE.ClampToEdgeWrapping;
  backTex.needsUpdate = true;
}
}

}, [
  sourceMesh,
  singleMat,
  rules,
  typeKey,
  onBounds,
  projectionFit,
  overlapMode,
  clipY,
  clipTarget,
  occFrontTex,
  occBackTex,
  occluderFrontSrc,
  occluderBackSrc,
  occluderMin,
  occluderMax,
  occluderCategory,
  occluderFitData,
  occTypeKey,
    occRules,
  debugMeshUV,
  useMeshUVMapping,
  frontTex,
backTex,
uvAtlasRange,
]);


  if (!renderRoot) return null;

return (
  <group
    ref={groupRef}
    position={[0, 0, (rules.zShift ?? 0)]}// keep only zShift if you need it
    rotation={[0, 0, 0]}
    scale={[1, 1, 1]}                        // HARD LOCK
  />
);

}

useGLTF.preload("/__missing__.glb");
